import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import Navbar from "./Navbar";
import Footer from "./Footer";
import PageTransition from "./ui/PageTransition";

interface LayoutProps {
  children: React.ReactNode;
  showBackButton?: boolean;
  title?: string;
  description?: string;
}

const Layout = ({
  children,
  showBackButton = false,
  title,
  description,
}: LayoutProps) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const pageTitle = title
    ? `${title} | 港微創 HK MicroLaunch`
    : "港微創 HK MicroLaunch";
  const pageDescription =
    description ||
    t("meta.description") ||
    "港微創 HK MicroLaunch — AI 驅動的港式 Marketing 自動化平台";

  return (
    <div className="min-h-screen flex flex-col bg-space text-gray-100 transition-colors duration-200 font-sans">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <html lang={t("lang") || "en"} />
      </Helmet>
      <Navbar />

      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageTransition>
          {(showBackButton || title) && (
            <div className="flex items-center gap-4 mb-6">
              {showBackButton && (
                <button
                  onClick={handleBack}
                  className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label={t("common.back") || "Back"}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                </button>
              )}
              {title && (
                <h1 className="text-h3 font-bold tracking-tight">{title}</h1>
              )}
            </div>
          )}
          {children}
        </PageTransition>
      </main>

      <Footer />
    </div>
  );
};

export default Layout;
